import { Link as RouterLink } from '@tanstack/react-router'
import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  type AnchorHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react'

type DevicesLinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  href?: string
  to?: string
  passHref?: boolean
  legacyBehavior?: boolean
  children?: ReactNode
}

function isExternalUrl(url: string) {
  return (
    url.startsWith('http') ||
    url.startsWith('mailto:') ||
    url.startsWith('tel:') ||
    url.startsWith('//')
  )
}

const DevicesLink = forwardRef<HTMLAnchorElement, DevicesLinkProps>(
  function DevicesLink(
    {
      href,
      to,
      passHref: _passHref,
      legacyBehavior,
      children,
      target,
      rel,
      ...props
    },
    ref,
  ) {
    const destination = String(to ?? href ?? '/')

    if (destination.startsWith('#') || isExternalUrl(destination)) {
      if (legacyBehavior && isValidElement(children)) {
        return cloneElement(children as ReactElement, {
          href: destination,
          target,
          rel,
          ref,
          ...props,
        })
      }

      return (
        <a ref={ref} href={destination} target={target} rel={rel} {...props}>
          {children}
        </a>
      )
    }

    if (legacyBehavior && isValidElement(children)) {
      return (
        <RouterLink
          to={destination}
          target={target}
          rel={rel}
          ref={ref}
          {...props}
        >
          {Children.only(children)}
        </RouterLink>
      )
    }

    return (
      <RouterLink
        ref={ref}
        to={destination}
        target={target}
        rel={rel}
        {...props}
      >
        {children}
      </RouterLink>
    )
  },
)

export default DevicesLink
